// i18n
import { translateInComponent } from 'infras/i18n';
import translations from '../i18n.json';
import * as tracker from 'presentations/views/helpers/analytics';

import { useState, SyntheticEvent, useEffect, useContext } from 'react';
import { useRouter } from 'next/router';

import { destroy } from 'infras/rest_client';
import { ViewerItem } from 'presentations/api/schema/user';
import { apiPath } from 'presentations/routes/path';
import { useChat } from 'react-live-chat-loader';
import { useRequest } from 'presentations/views/hooks/request';
import { ApiKeyOptionIndex } from 'presentations/views/components/organisms/ApiKeyList';
import {
  DashboardPortalContext,
  setErrorMessage,
  setSuccessMessage,
} from 'presentations/reducers/DashboardPortal';
import {
  ApiKeyListItem,
  ApiKeyResourceItem,
} from 'presentations/api/schema/api_key';

interface Props {
  viewer: ViewerItem;
}
const perPage = 20;

export function useApiKeyListPage(props: Props) {
  const t = translateInComponent(translations);

  const { state: navigationState, dispatch: navigationDispatch } = useContext(
    DashboardPortalContext,
  );

  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [editApiKey, setEditApiKey] = useState<ApiKeyListItem | null>(null);
  const [deleteApiKey, setDeleteApiKey] = useState<ApiKeyListItem | null>(null);
  const [anchor, setAnchor] = useState<any>(null);
  const [isAccountLoading, setIsAccountLoading] = useState<boolean>(false);
  const [isShowingApiKeyDialog, setIsShowingApiKeyDialog] = useState<boolean>(
    false,
  );

  const router = useRouter();
  const [state, loadChat] = useChat();

  useEffect(() => {
    loadChat({ open: false });
    window.Intercom &&
      window.Intercom('update', { hide_default_launcher: false });

    return function () {
      window.Intercom &&
        window.Intercom('update', { hide_default_launcher: true });
    };
  }, []);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    if (router.query.created) {
      navigationDispatch(setSuccessMessage(t('Add api key')));
    } else if (router.query.updated) {
      navigationDispatch(setSuccessMessage(t('Update api key')));
    }
  }, [router.isReady, router.query.created, router.query.updated]);

  const apiKeysRequest = useRequest(
    apiPath(`/v1/teams/${props.viewer.team!.uid}/api_keys`, {
      page: currentPage,
      perPage,
    }),
  );
  const apiKeys: ApiKeyResourceItem[] = apiKeysRequest.data || [];

  const onChangePage = async (e: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const onDelete = async () => {
    if (!deleteApiKey) return;
    setIsConfirmLoading(true);
    const result = await destroy(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/api_keys/${deleteApiKey.uid}`,
      ),
    );
    if (!result.isSuccess) {
      setDeleteApiKey(null);
      setIsConfirmLoading(false);
      navigationDispatch(setErrorMessage(t('Failed Delete api key')));
      return;
    }

    const newApiKeys = apiKeys.filter((e) => e.uid !== deleteApiKey.uid);
    apiKeysRequest.mutate({ ...apiKeysRequest, data: newApiKeys }, false);
    setDeleteApiKey(null);
    setIsConfirmLoading(false);
    navigationDispatch(setSuccessMessage(t('Delete api key')));
    tracker.setEvent('api key delete');
  };

  const onUpdate = async () => {
    apiKeysRequest.mutate();
    setEditApiKey(null);
    setIsShowingApiKeyDialog(false);
    setIsConfirmLoading(false);
    navigationDispatch(setSuccessMessage(t('Update api key')));
    tracker.setEvent('api key update');
  };

  const onCreate = async () => {
    apiKeysRequest.mutate();
    tracker.setEvent('api key create');
  };

  const onClickNewApiKey = async (e: SyntheticEvent<HTMLButtonElement>) => {
    setIsShowingApiKeyDialog(true);
  };

  const onCloseDialog = () => {
    setDeleteApiKey(null);
    setIsShowingApiKeyDialog(false);
  };

  const onSelectMenu = async (
    apiKey: ApiKeyListItem,
    selectedIndex: number,
  ) => {
    switch (selectedIndex) {
      case ApiKeyOptionIndex.edit: {
        setEditApiKey(apiKey);
        setIsShowingApiKeyDialog(true);
        break;
      }
      case ApiKeyOptionIndex.delete: {
        setDeleteApiKey(apiKey);
        break;
      }
      default:
        break;
    }
  };

  return {
    state: {
      apiKeys,
      isAccountLoading,
      isLoading: apiKeysRequest.isLoading,
      currentPage,
      numberOfPages: apiKeysRequest.numberOfPages,
      isConfirmLoading,
      editApiKey,
      deleteApiKey,
      anchor,
      isShowingApiKeyDialog,
    },
    actions: {
      onChangePage,
      onSelectMenu,
      onDelete,
      onCloseDialog,
      onClickNewApiKey,
      onUpdate,
      onCreate,
    },
  };
}
